import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createChargingSite, addChargingSiteThirdPartySystemMapping, updateChargingSite } from "../../api/ChargingSites";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import SiteForm from "../../components/site/SiteForm";
import SiteThirdPartyRef from "../../components/site/SiteThirdPartyRef";
import SiteGridSettings from "../../components/gridCost/SiteGridSettings";
import { Roles } from "../../providers/AuthContext";
import { useTranslation } from "react-i18next";

const CreateSitePage = () => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth(); // Get the access token from context
  const navigate = useNavigate();
  const isAdmin = hasRole?.(Roles.Admin) ?? false;
  const [invalidFields, setInvalidFields] = useState([]);

  // Define initial state for the form
  const [chargingSite, setChargingSite] = useState({
    name: "",
    address: "",
    city: "",
    zipCode: "",
    latitude: 0,
    longitude: 0,
    countryCode: "",
    timeZone: "",
    biddingZonePublicId: "",
    gridAreaPublicId: "",
    connectionKw: "",
    gridVoltageLevel: ""
    
  });
  const [thirdPartySystemMapping, setThirdPartySystemMapping] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [heavyVehicleData] = useState({
    suitability: "TRUCK_TRAILER",
    trailer_parking: false,
    truck_only: false,
    height_restriction: false,
    height: "0",
    width: "0",
    length: "0",
    mass: "0",
    cable_side: [],
    drive_through: false,
    dangerous_goods: false,
    rigid_vehicles: false,
    overnight_parking: false,
    amenities: [],
    barrier: false,
    cable_length: "0",
    security_measures: [],
    turning_radius: "0",
  });

 const handleInputChange = (key, value) => {
    setHasChanges(true);
    setChargingSite((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreateChargingSite = async () => {
    const { name, address, city, zipCode, countryCode, timeZone } = chargingSite;

      const missing = [];
  if (!name) missing.push("name");
  if (!address) missing.push("address");
  if (!city) missing.push("city");
  if (!zipCode) missing.push("zipCode");
  if (!countryCode) missing.push("countryCode");
  if (!timeZone) missing.push("timeZone");

  if (missing.length > 0) {
    setInvalidFields(missing);
    toast.error(t("common.pleaseFillRequiredFields", { fields: missing.join(", ") }));
    return;
  }

  setInvalidFields([]);

    const toastId = toast.loading(t("toast.creating", { entity: t("entity.chargingSite") }));
    setLoading(true);

    try {
      const accessToken = await getAccessToken();
      const newChargingSite = await createChargingSite(chargingSite, accessToken); // Make the API call to create a site
      setChargingSite(newChargingSite);
      const hwData = { ...newChargingSite, heavyVehicle: heavyVehicleData };
      await updateChargingSite(hwData, accessToken);
      // Create third-party system mappings in parallel
      const thirdPartySystemPromises = thirdPartySystemMapping
        .filter((thirdPartySystem) => thirdPartySystem.isNew)
        .map(async (thirdPartySystem) => {
          thirdPartySystem.chargingSitePublicId = newChargingSite.chargingSitePublicId;
          return addChargingSiteThirdPartySystemMapping(thirdPartySystem, accessToken);
        });

      await Promise.all(thirdPartySystemPromises);

      toast.success(t("toast.created", { entity: t("entity.chargingSite") }), { id: toastId });
      navigate("/site/" + newChargingSite.chargingSitePublicId); 
    } catch (error) {
        toast.error(error?.message || t("toast.failedCreate", { entity: t("entity.site") }), {
        id: toastId,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage title={t("entity.sites")} loading={loading} />
          </div>

          <div className={`${loading ? "loading" : ""} page-content`}>
            <div className="flex flex-row items-center pb-4">
              <button
                onClick={() => navigate("/sites")}
                className="text-black flex"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">{t("pages.createSite")}</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigate("/sites")}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!chargingSite.name}
                  onClick={handleCreateChargingSite}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            {/* Charging site form */}
            <div className="flex flex-col px-4 py-8 shadow-lg rounded-md w-full gap-4">
              <SiteForm 
              chargingSite={chargingSite} 
              setChargingSite={setChargingSite} 
              setHasChanges={setHasChanges} 
              handleInputChange={handleInputChange} 
              isCreate={true} 
              invalidFields={invalidFields}
              />

              <SiteGridSettings
                chargingSite={chargingSite}
                setChargingSite={setChargingSite}
                setHasChanges={setHasChanges}
              />

              <div className="flex w-full">
                {/* Third-party system table */}
                {isAdmin && (
                  <SiteThirdPartyRef
                    hasChanges={hasChanges}
                    thirdPartySystemMapping={thirdPartySystemMapping}
                    setThirdPartySystemMapping={setThirdPartySystemMapping}
                    isCreate={true}
                    handleInputChange={handleInputChange}
                  />
                )}
              </div>


            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateSitePage;
